// ============================================================
// STATE MANAGER — Server-side authoritative API
// ============================================================
//
// Public surface exposed to the network transport layer. All
// mutating methods return a NEW ServerState; the manager itself
// only holds a reference so callers that want a stateful wrapper
// have an ergonomic path, while Phase 3 tests can still drive the
// pure functions directly.
//
// Responsibilities covered (from session brief):
//   1. validate swap (Phase 1 RequestSwap)
//   2. maintain RoundMatchStats per team
//   3. execute the 3-Tick Execution phase (pure, deterministic)
//
// The current single-machine BattleDirector remains the Client's
// production path; StateManager is consumed by the Phase 3 server
// and by unit tests.

import type { BattleUnit, Base } from '../BattleUnit.ts';
import { cloneState } from './clone.ts';
import { executeExec, type ExecResult } from './tick.ts';
import { validateCardSchedule } from './validation.ts';
import { generateTerrain } from './terrain.ts';
import type {
  BattleEvent,
  ServerState,
  SpellCommand,
  TickCommands,
  ValidationResult,
} from './types.ts';
import { spellsForTeam } from './types.ts';

export class StateManager {
  /** Authoritative battle state. */
  state: ServerState;

  constructor(initialUnits: BattleUnit[], initialBases: Base[]) {
    this.state = {
      units: initialUnits,
      bases: initialBases,
      phase: 'match3',
      round: 0,
      playerStats: {},
      aiStats: {},
      playerUsedEls: [],
      aiUsedEls: [],
    };
    // Normalise via cloneState so downstream mutations never leak into
    // caller-owned unit instances.
    this.state = cloneState(this.state);
  }

  // ---------------------------------------------------------------
  // Terrain
  // ---------------------------------------------------------------

  /**
   * Stamp a terrain seed onto the state and regenerate `state.terrain`
   * deterministically from it. Idempotent — calling twice with the same
   * seed yields the same grid; calling with a different seed replaces
   * the grid entirely.
   *
   * Callers (single: LocalServerAdapter / main.ts handleInitBattle;
   * PvP: RoomBattle.tryStart) invoke this once per battle, right after
   * construction. The pure function is also exported by `terrain.ts` so
   * clients can compute their own copy from the same seed without
   * involving the StateManager.
   */
  applyTerrainSeed(seed: number): void {
    const s = (seed >>> 0);
    this.state.terrainSeed = s;
    this.state.terrain = generateTerrain(s);
  }


  /**
   * Card Tactician (M3) — flip this state into card-mode validation. Once
   * on, `validateSchedule` / `executeExec` use `validateCardSchedule`
   * (hand-driven elements, no Points & Tiers / leader gate). Called once at
   * battle init by the transport layer (single: LocalServerAdapter.initBattle
   * when Game is in single-player card mode). Idempotent.
   */
  setCardMode(on: boolean): void {
    this.state.cardMode = on;
  }

  // ---------------------------------------------------------------
  // Round entry (Card Tactician)
  // ---------------------------------------------------------------

  /** Transition into the spell-scheduling phase. Called by the transport
   *  layer at the start of a round's planning window. */
  enterScheduling(): ServerState {
    const next = cloneState(this.state);
    next.phase = 'sched';
    this.state = next;
    return next;
  }

  // ---------------------------------------------------------------
  // Spell scheduling (card-mode validation)
  // ---------------------------------------------------------------

  /** Validate a team's 3-slot card schedule (structure only). Deck-ownership
   *  (does the team hold these cards?) is checked at the netserver layer by
   *  RoomBattle against its authoritative deck (PvP anti-cheat). */
  validateSchedule(_team: 0 | 1, schedule: SpellCommand[]): ValidationResult {
    return validateCardSchedule(schedule);
  }

  // ---------------------------------------------------------------
  // Execution
  // ---------------------------------------------------------------

  /**
   * Run the 3-Tick deterministic execution and return the event log.
   * Mutates internal state; returns the new state and events.
   *
   * Equivalent to BattleDirector.buildTimeline + Save/Restore in a
   * single call — but here the snapshot-and-restore is baked into
   * `cloneState`, and the emitted `BattleEvent[]` is plain data that
   * can be serialised or replayed deterministically.
   */
  executeExec(cmds: TickCommands): { events: BattleEvent[]; winner: number } {
    // Validate both schedules up front so a malformed plan from EITHER
    // side can't silently clobber the game.
    for (const team of [0, 1] as const) {
      const res = validateCardSchedule(spellsForTeam(cmds, team));
      if (!res.valid) throw new Error(`invalid team ${team} schedule: ${res.reason}`);
    }

    const result: ExecResult = executeExec(this.state, cmds);
    this.state = result.newState;
    return { events: result.events, winner: result.winner };
  }

  /**
   * Stateless variant — same logic as `executeExec` but does not touch
   * `this.state`. Handy for speculative simulation and tests.
   */
  static executeExec(state: ServerState, cmds: TickCommands): ExecResult {
    return executeExec(state, cmds);
  }
}
